// SNOWCLUB-LAUNCH-REMAINING-PARITY-001 · Gold content-equity correction (exact-candidate audit of c11131dd).
//
// THE MEASURED GAP. On the transport-information families the ranking page is an EXPLANATORY page — the
// destination, its gateway airport, how to book, the FAQs — and the candidate served all of that only in
// the [data-sg-server-layer] block, which the visitor's browser hides once the design paints. What a
// visitor (and rendered Googlebot) then saw was the design's booking surface alone: 10 of 10 Gold
// /bus-schedule pages and 8 of 8 Gold /bus-schedule/airport-information pages retained 10-17% of the
// legacy page's visible vocabulary, every one of them with the full copy sitting one hidden layer down.
//
// THE RENDER CONTRACT, corrected once for the family rather than page by page: the intent-bearing part of
// the page's OWN extraction record is painted where the visitor can read it — inside the design's own
// scroll container, above its footer — by the same clone mechanism SiteLinkNavPaint already uses. Nothing
// is authored here; this file only DECIDES WHICH captured blocks are fit to paint, and says why not for
// each one it leaves out. Token retention was the detector; these rules are the editorial decision.
//
// PURE: no fs, no fetch, no DOM. The server marks the blocks; the client clones the marked ones.

export interface PaintBlock { type?: string; level?: number; text?: string }
export interface PaintRemoval { index: number; reason: "chrome" | "not-text" | "static-fare" | "duplicate-of-heading" | "heading-without-body" }

/** The families whose painted surface was measured losing the page's explanatory content. One template
 *  each, all roster mountains. Every other page type was reviewed and keeps its painted surface as is
 *  (docs/evidence/snowclub-launch-remaining-parity-001/GOLD-27-PAGE-REVIEW.md). */
const PAINTED_CONTENT_FAMILIES: readonly RegExp[] = [
  /^mountain-[a-z0-9-]+_bus-schedule$/,
  /^mountain-[a-z0-9-]+_bus-schedule_airport-information$/,
  /^mountain-[a-z0-9-]+_bus-schedule_general-information$/,
];
export const isPaintedContentSlug = (slug: string) => PAINTED_CONTENT_FAMILIES.some((re) => re.test(slug));

/** Live's footer columns open with this label in every capture; everything from it on is site chrome
 *  the design's own footer already carries. */
const FOOTER_CHROME_START = "Our Services";
/** A captured figure is a STATIC fare — the design paints the engine's live fare on the same screen, and
 *  two prices for one seat is the contradiction fare-truth-visible-gate exists to refuse. */
const STATIC_FARE = /\$\s?\d/;
const clean = (v: unknown) => (typeof v === "string" ? v.replace(/\s+/g, " ").trim() : "");
const isBody = (b: PaintBlock) => b.type === "paragraph" || b.type === "listitem";

/** Which block indexes are painted, in order, and why each other in-range block is not. */
export function paintedContentIndexes(blocks: readonly PaintBlock[]): { keep: number[]; removed: PaintRemoval[] } {
  const removed: PaintRemoval[] = [];
  const start = blocks.findIndex((b) => b?.type === "heading");
  if (start < 0) return { keep: [], removed };
  const chromeAt = blocks.findIndex((b, i) => i > start && b?.type === "paragraph" && clean(b.text) === FOOTER_CHROME_START);
  const end = chromeAt < 0 ? blocks.length : chromeAt;
  for (let i = end; i < blocks.length; i++) removed.push({ index: i, reason: "chrome" });

  const candidate: number[] = [];
  let lastHeading = "";
  for (let i = start; i < end; i++) {
    const b = blocks[i];
    if (!b) continue;
    const text = clean(b.text);
    if (b.type === "heading") lastHeading = text;
    if (b.type !== "heading" && !isBody(b)) { removed.push({ index: i, reason: "not-text" }); continue; }
    if (!text) { removed.push({ index: i, reason: "not-text" }); continue; }
    if (STATIC_FARE.test(text)) { removed.push({ index: i, reason: "static-fare" }); continue; }
    // The capture repeats a section heading as its first paragraph; painted once, not twice.
    if (isBody(b) && text === lastHeading) { removed.push({ index: i, reason: "duplicate-of-heading" }); continue; }
    candidate.push(i);
  }
  // A heading with no body under it is a label of a widget whose cells did not survive (live's operator
  // comparison grid: "Pick-Up Spots", "Average Fare", …). Bottom-up, so a parent left with only such
  // labels goes too, while a parent that still leads real copy stays.
  let keep = candidate;
  for (;;) {
    const next = keep.filter((idx, pos) => {
      const b = blocks[idx];
      if (b?.type !== "heading") return true;
      const level = b.level ?? 6;
      for (let p = pos + 1; p < keep.length; p++) {
        const n = blocks[keep[p] ?? -1];
        if (!n) continue;
        if (n.type === "heading" && (n.level ?? 6) <= level) return false;
        if (isBody(n)) return true;
      }
      return false;
    });
    if (next.length === keep.length) break;
    keep = next;
  }
  const kept = new Set(keep);
  for (const idx of candidate) if (!kept.has(idx)) removed.push({ index: idx, reason: "heading-without-body" });
  removed.sort((a, b) => a.index - b.index);
  return { keep, removed };
}

/** The tag a captured heading is served with. ExportShell owns the document's one <h1>, so the record's
 *  own level-1 heading leads the copy as an <h2>; live's h2/h3 keep their level; deeper caps at h4. */
export const headingTag = (level: number | undefined): "h2" | "h3" | "h4" =>
  !level || level <= 2 ? "h2" : level === 3 ? "h3" : "h4";
